/**
 * Layout Manager - Handles panel resizing, layout toggle, and fullscreen
 */

// Constants
const MIN_PANEL_RATIO = 0.2;
const MAX_PANEL_RATIO = 0.8;
const DEFAULT_PANEL_RATIO = 0.5;

export class LayoutManager {
  constructor(options = {}) {
    this.onResize = options.onResize || (() => {});
    this.onLayoutChange = options.onLayoutChange || (() => {});

    // Layout state (can be initialized from saved settings)
    this.layoutMode = options.initialLayout || 'horizontal'; // 'horizontal', 'vertical', 'overlay'
    this.isVertical = this.layoutMode === 'vertical';
    this.isOverlay = this.layoutMode === 'overlay';
    this.isSwapped = options.initialSwapped || false;
    this.isFullscreen = false;
    this.isDragging = false;
    this.panelRatio = DEFAULT_PANEL_RATIO;

    // DOM elements (cached on init)
    this.mainContent = null;
    this.resizeHandle = null;
    this.previewPanel = null;
    this.layoutHorizontalBtn = null;
    this.layoutVerticalBtn = null;
    this.layoutOverlayBtn = null;
    this.swapBtn = null;
    this.fullscreenBtn = null;

    // Bound handlers for cleanup
    this.boundHandlers = {};
  }

  init() {
    // Cache DOM elements
    this.mainContent = document.getElementById('main-content');
    this.resizeHandle = document.getElementById('resize-handle');
    this.previewPanel = document.getElementById('panel-preview');
    this.layoutHorizontalBtn = document.getElementById('btn-layout-horizontal');
    this.layoutVerticalBtn = document.getElementById('btn-layout-vertical');
    this.layoutOverlayBtn = document.getElementById('btn-layout-overlay');
    this.swapBtn = document.getElementById('btn-swap');
    this.fullscreenBtn = document.getElementById('btn-fullscreen');

    // Apply initial state from saved settings
    this.applyInitialState();

    this.setupEventListeners();
  }

  applyInitialState() {
    // Apply layout mode
    this.updateLayoutClasses();
    this.updateLayoutButtonStates();

    // Apply swapped state if needed
    if (this.isSwapped) {
      this.mainContent.classList.add('swapped');
      this.swapBtn?.classList.add('active');
    }

    this.updateGridLayout();
  }

  updateLayoutClasses() {
    this.mainContent.classList.remove('vertical', 'overlay');
    if (this.isVertical) {
      this.mainContent.classList.add('vertical');
    } else if (this.isOverlay) {
      this.mainContent.classList.add('overlay');
    }
  }

  updateLayoutButtonStates() {
    this.layoutHorizontalBtn?.classList.toggle('active', !this.isVertical && !this.isOverlay);
    this.layoutVerticalBtn?.classList.toggle('active', this.isVertical);
    this.layoutOverlayBtn?.classList.toggle('active', this.isOverlay);
  }

  setupEventListeners() {
    // Layout toggle buttons
    this.layoutHorizontalBtn?.addEventListener('click', () => this.setLayoutMode('horizontal'));
    this.layoutVerticalBtn?.addEventListener('click', () => this.setLayoutMode('vertical'));
    this.layoutOverlayBtn?.addEventListener('click', () => this.setLayoutMode('overlay'));

    // Swap panels
    this.swapBtn?.addEventListener('click', () => this.toggleSwap());

    // Fullscreen
    this.fullscreenBtn.addEventListener('click', () => this.toggleFullscreen());

    // Exit fullscreen button
    const exitFullscreenBtn = document.getElementById('btn-exit-fullscreen');
    exitFullscreenBtn.addEventListener('click', () => this.toggleFullscreen());

    // ESC to exit fullscreen (document-level, needs cleanup)
    this.boundHandlers.keydown = (e) => {
      if (e.key === 'Escape' && this.isFullscreen) {
        this.toggleFullscreen();
      }
    };
    document.addEventListener('keydown', this.boundHandlers.keydown);

    // Resize handle
    this.setupResizeHandler();
  }

  setupResizeHandler() {
    // Mouse events
    this.resizeHandle.addEventListener('mousedown', (e) => {
      e.preventDefault();
      this.startDrag();
    });

    // Document-level mouse events (need cleanup)
    this.boundHandlers.mousemove = (e) => {
      if (!this.isDragging) return;
      this.handleDrag(e.clientX, e.clientY);
    };
    this.boundHandlers.mouseup = () => {
      this.endDrag();
    };
    document.addEventListener('mousemove', this.boundHandlers.mousemove);
    document.addEventListener('mouseup', this.boundHandlers.mouseup);

    // Touch events
    this.resizeHandle.addEventListener('touchstart', (e) => {
      e.preventDefault();
      this.startDrag();
    }, { passive: false });

    // Document-level touch events (need cleanup)
    this.boundHandlers.touchmove = (e) => {
      if (!this.isDragging) return;
      const touch = e.touches[0];
      this.handleDrag(touch.clientX, touch.clientY);
    };
    this.boundHandlers.touchend = () => {
      this.endDrag();
    };
    document.addEventListener('touchmove', this.boundHandlers.touchmove, { passive: true });
    document.addEventListener('touchend', this.boundHandlers.touchend);
  }

  startDrag() {
    this.isDragging = true;
    this.resizeHandle.classList.add('dragging');
    document.body.style.cursor = this.isVertical ? 'row-resize' : 'col-resize';
    document.body.style.userSelect = 'none';
  }

  handleDrag(clientX, clientY) {
    const rect = this.mainContent.getBoundingClientRect();

    // Guard against zero dimensions
    if (rect.width === 0 || rect.height === 0) return;

    let ratio;

    if (this.isVertical) {
      ratio = (clientY - rect.top) / rect.height;
    } else {
      ratio = (clientX - rect.left) / rect.width;
    }

    // When swapped, invert the ratio to maintain intuitive drag direction
    if (this.isSwapped) {
      ratio = 1 - ratio;
    }

    // Clamp ratio
    ratio = Math.max(MIN_PANEL_RATIO, Math.min(MAX_PANEL_RATIO, ratio));
    this.panelRatio = ratio;
    this.updateGridLayout();
  }

  endDrag() {
    if (this.isDragging) {
      this.isDragging = false;
      this.resizeHandle.classList.remove('dragging');
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      this.onResize();
    }
  }

  updateGridLayout() {
    // Overlay mode uses absolute positioning, no grid needed
    if (this.isOverlay) {
      this.mainContent.style.gridTemplateColumns = '';
      this.mainContent.style.gridTemplateRows = '';
      return;
    }

    // When swapped, invert the ratio since CSS order flips the visual position
    const ratio1 = this.isSwapped ? 1 - this.panelRatio : this.panelRatio;
    const ratio2 = this.isSwapped ? this.panelRatio : 1 - this.panelRatio;

    if (this.isVertical) {
      this.mainContent.style.gridTemplateColumns = '1fr';
      this.mainContent.style.gridTemplateRows = `${ratio1}fr 4px ${ratio2}fr`;
    } else {
      this.mainContent.style.gridTemplateColumns = `${ratio1}fr 4px ${ratio2}fr`;
      this.mainContent.style.gridTemplateRows = '';
    }
  }

  setLayoutMode(mode) {
    if (this.layoutMode === mode) return;

    this.layoutMode = mode;
    this.isVertical = mode === 'vertical';
    this.isOverlay = mode === 'overlay';

    this.updateLayoutClasses();
    this.updateLayoutButtonStates();

    // Update swap button state
    this.updateSwapButtonState();

    this.updateGridLayout();
    this.triggerResize();

    // Notify about layout change
    this.onLayoutChange({
      layout: this.layoutMode,
      swapped: this.isSwapped,
    });
  }

  // Legacy method for compatibility
  setLayout(vertical) {
    this.setLayoutMode(vertical ? 'vertical' : 'horizontal');
  }

  toggleLayout() {
    this.setLayoutMode(this.isVertical ? 'horizontal' : 'vertical');
  }

  toggleSwap() {
    this.isSwapped = !this.isSwapped;
    this.mainContent.classList.toggle('swapped', this.isSwapped);

    // Update swap button state
    this.updateSwapButtonState();

    // Update grid to maintain panel sizes visually
    this.updateGridLayout();
    this.triggerResize();

    // Notify about layout change
    this.onLayoutChange({
      layout: this.layoutMode,
      swapped: this.isSwapped,
    });
  }

  updateSwapButtonState() {
    this.swapBtn?.classList.toggle('active', this.isSwapped);
  }

  toggleFullscreen() {
    this.isFullscreen = !this.isFullscreen;
    this.previewPanel.classList.toggle('fullscreen', this.isFullscreen);
    this.updateFullscreenIcon();
    this.triggerResize();
  }

  updateFullscreenIcon() {
    if (!this.fullscreenBtn) return;

    const expandIcon = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <polyline points="15 3 21 3 21 9"/>
      <polyline points="9 21 3 21 3 15"/>
      <line x1="21" y1="3" x2="14" y2="10"/>
      <line x1="3" y1="21" x2="10" y2="14"/>
    </svg>`;

    const minimizeIcon = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <polyline points="4 14 10 14 10 20"/>
      <polyline points="20 10 14 10 14 4"/>
      <line x1="14" y1="10" x2="21" y2="3"/>
      <line x1="3" y1="21" x2="10" y2="14"/>
    </svg>`;

    this.fullscreenBtn.innerHTML = this.isFullscreen ? minimizeIcon : expandIcon;
    this.fullscreenBtn.title = this.isFullscreen ? 'Exit fullscreen' : 'Fullscreen';
  }

  triggerResize() {
    setTimeout(() => this.onResize(), 0);
  }

  /**
   * Clean up event listeners
   */
  destroy() {
    // Remove document-level event listeners
    if (this.boundHandlers.keydown) {
      document.removeEventListener('keydown', this.boundHandlers.keydown);
    }
    if (this.boundHandlers.mousemove) {
      document.removeEventListener('mousemove', this.boundHandlers.mousemove);
    }
    if (this.boundHandlers.mouseup) {
      document.removeEventListener('mouseup', this.boundHandlers.mouseup);
    }
    if (this.boundHandlers.touchmove) {
      document.removeEventListener('touchmove', this.boundHandlers.touchmove);
    }
    if (this.boundHandlers.touchend) {
      document.removeEventListener('touchend', this.boundHandlers.touchend);
    }
    this.boundHandlers = {};
  }
}
